/**
 * WelcomeModals
 *
 * Two sequential entrance modals:
 *  1. AgeGate — age verification + responsible gaming warning (blocking)
 *  2. ReferralPromo — referral program teaser (shown after age gate)
 *
 * Persistence via localStorage:
 *   bolcoin_age_ok     — '1' when user checked "no volver a mostrar" on age gate
 *   bolcoin_ref_shown  — '1' when user checked "no volver a mostrar" on referral promo
 *
 * Not rendered on /admin/* routes.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWeb3 } from '../../contexts/Web3Context';
import { getMyReferralInfo } from '../../api/referralApi';
import './WelcomeModals.css';

const AGE_KEY = 'bolcoin_age_ok';
const REF_KEY = 'bolcoin_ref_shown';

/* ── Icons ─────────────────────────────────────────────────────────── */
const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const GiftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 12 20 22 4 22 4 12" />
    <rect x="2" y="7" width="20" height="5" />
    <line x1="12" y1="22" x2="12" y2="7" />
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
  </svg>
);

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const WarningIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

/* ── Checkbox helper ─────────────────────────────────────────────── */
function ModalCheckbox({ id, checked, onChange, label }) {
  return (
    <label className="wm-checkbox" htmlFor={id}>
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={e => onChange(e.target.checked)}
      />
      <span className="wm-checkbox__box" aria-hidden="true" />
      <span className="wm-checkbox__label">{label}</span>
    </label>
  );
}

/* ── Main component ─────────────────────────────────────────────── */
export function WelcomeModals() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isConnected } = useWeb3();

  const [showAge, setShowAge] = useState(false);
  const [showRef, setShowRef] = useState(false);

  // Age gate state
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [ageNoShow, setAgeNoShow] = useState(false);

  // Referral modal state
  const [refNoShow, setRefNoShow] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [copied, setCopied] = useState(false);

  const isAdmin = location.pathname.startsWith('/admin');

  // ── Init: decide which modals to show ───────────────────────────
  useEffect(() => {
    if (isAdmin) return;

    if (!localStorage.getItem(AGE_KEY)) {
      setShowAge(true);
    } else if (!localStorage.getItem(REF_KEY)) {
      // Age already verified on a prev session — show referral after short delay
      const t = setTimeout(() => setShowRef(true), 1200);
      return () => clearTimeout(t);
    }
  }, [isAdmin]);

  // ── Load referral data when wallet is connected ──────────────────
  useEffect(() => {
    if (!isConnected || !showRef) return;
    getMyReferralInfo()
      .then(info => setReferralData(info))
      .catch(() => {});
  }, [isConnected, showRef]);

  // ── Age gate handlers ────────────────────────────────────────────
  const handleAgeConfirm = useCallback(() => {
    if (!ageConfirmed) return;
    if (ageNoShow) localStorage.setItem(AGE_KEY, '1');
    setShowAge(false);
    if (!localStorage.getItem(REF_KEY)) {
      setTimeout(() => setShowRef(true), 500);
    }
  }, [ageConfirmed, ageNoShow]);

  const handleAgeReject = useCallback(() => {
    window.location.href = 'https://www.google.com';
  }, []);

  // ── Referral modal handlers ──────────────────────────────────────
  const handleRefClose = useCallback(() => {
    if (refNoShow) localStorage.setItem(REF_KEY, '1');
    setShowRef(false);
  }, [refNoShow]);

  const handleRefGoTo = useCallback(() => {
    if (refNoShow) localStorage.setItem(REF_KEY, '1');
    setShowRef(false);
    navigate('/referrals');
  }, [refNoShow, navigate]);

  const handleCopy = useCallback(() => {
    const link = referralData?.code
      ? `${window.location.origin}/referrals?ref=${referralData.code}`
      : `${window.location.origin}/referrals`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [referralData]);

  if (isAdmin) return null;

  const referralLink = referralData?.code
    ? `${window.location.origin}/referrals?ref=${referralData.code}`
    : null;

  return (
    <>
      {/* ── Modal 1: Age Gate ───────────────────────────────────────── */}
      {showAge && (
        <div className="wm-overlay" role="dialog" aria-modal="true"
          aria-labelledby="wm-age-title">
          <div className="wm-card wm-card--age">

            {/* Badge */}
            <div className="wm-badge wm-badge--warning">
              <WarningIcon />
              <span>Acceso restringido</span>
            </div>

            {/* Icon */}
            <div className="wm-icon wm-icon--shield">
              <ShieldIcon />
            </div>

            {/* Title */}
            <h2 id="wm-age-title" className="wm-title">
              Plataforma de juegos de azar
            </h2>
            <p className="wm-subtitle">
              Estás a punto de acceder a <strong>Bolcoin</strong>, una plataforma de
              apuestas y juegos de azar descentralizada que opera en la red
              blockchain de <strong>Polygon</strong> con USDT como moneda de juego.
            </p>

            {/* Info pills */}
            <div className="wm-pills">
              <span className="wm-pill">🎲 Keno · Bingo · La Bolita</span>
              <span className="wm-pill">⛓ On-chain — Chainlink VRF</span>
              <span className="wm-pill">💰 USDT (Polygon)</span>
            </div>

            {/* Responsible gaming box */}
            <div className="wm-warning-box">
              <div className="wm-warning-box__icon">
                <WarningIcon />
              </div>
              <div>
                <p className="wm-warning-box__title">Juega responsablemente</p>
                <p className="wm-warning-box__text">
                  El juego puede crear adicción. Solo apuesta lo que puedes
                  permitirte perder. Si crees que tienes un problema, busca ayuda
                  en&nbsp;
                  <a
                    href="https://www.gamblingtherapy.org"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Gambling Therapy
                  </a>.
                </p>
              </div>
            </div>

            {/* Age confirmation */}
            <div className="wm-confirm-section">
              <ModalCheckbox
                id="wm-age-check"
                checked={ageConfirmed}
                onChange={setAgeConfirmed}
                label="Confirmo que soy mayor de 18 años y que el juego de azar es legal en mi jurisdicción."
              />
              <ModalCheckbox
                id="wm-age-noshow"
                checked={ageNoShow}
                onChange={setAgeNoShow}
                label="No volver a mostrar este aviso."
              />
            </div>

            {/* Actions */}
            <div className="wm-actions">
              <button
                className="wm-btn wm-btn--primary"
                onClick={handleAgeConfirm}
                disabled={!ageConfirmed}
              >
                Confirmar y entrar
              </button>
              <button
                className="wm-btn wm-btn--ghost"
                onClick={handleAgeReject}
              >
                No tengo 18 años
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── Modal 2: Referral Promo ─────────────────────────────────── */}
      {showRef && !showAge && (
        <div className="wm-overlay wm-overlay--ref" role="dialog" aria-modal="true"
          aria-labelledby="wm-ref-title">
          <div className="wm-card wm-card--ref">

            {/* Close button */}
            <button className="wm-close" onClick={handleRefClose} aria-label="Cerrar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {/* Icon */}
            <div className="wm-icon wm-icon--gift">
              <GiftIcon />
            </div>

            {/* Title */}
            <h2 id="wm-ref-title" className="wm-title">
              ¡Gana invitando amigos!
            </h2>
            <p className="wm-subtitle">
              Comparte tu código único y gana una{' '}
              <strong className="wm-highlight">comisión del {referralData?.config?.bonusPercent ?? 3}%</strong>{' '}
              por cada apuesta que realicen las personas que invites.
              Sin límite de referidos, sin fecha de expiración.
            </p>

            {/* Steps */}
            <div className="wm-steps">
              <div className="wm-step">
                <span className="wm-step__num">1</span>
                <div>
                  <strong>Comparte tu link</strong>
                  <p>Envíalo por WhatsApp, Telegram o redes sociales.</p>
                </div>
              </div>
              <div className="wm-step">
                <span className="wm-step__num">2</span>
                <div>
                  <strong>Tu amigo juega</strong>
                  <p>Se registra con tu código y empieza a apostar.</p>
                </div>
              </div>
              <div className="wm-step">
                <span className="wm-step__num">3</span>
                <div>
                  <strong>Cobras comisión</strong>
                  <p>Las ganancias llegan directamente a tu wallet.</p>
                </div>
              </div>
            </div>

            {/* Referral code / link box */}
            {isConnected ? (
              referralLink ? (
                <div className="wm-ref-box">
                  <div className="wm-ref-box__label">
                    <UsersIcon />
                    <span>Tu código:&nbsp;<strong className="wm-code">{referralData.code}</strong></span>
                  </div>
                  <div className="wm-ref-link-row">
                    <code className="wm-ref-link">{referralLink}</code>
                    <button className="wm-copy-btn" onClick={handleCopy} title="Copiar link">
                      {copied ? (
                        <span className="wm-copied">¡Copiado!</span>
                      ) : (
                        <CopyIcon />
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="wm-ref-loading">Cargando tu código...</div>
              )
            ) : (
              <div className="wm-ref-box wm-ref-box--no-wallet">
                <UsersIcon />
                <p>Conecta tu wallet para obtener tu código de referido personalizado.</p>
              </div>
            )}

            {/* No mostrar again */}
            <ModalCheckbox
              id="wm-ref-noshow"
              checked={refNoShow}
              onChange={setRefNoShow}
              label="No volver a mostrar este anuncio."
            />

            {/* Actions */}
            <div className="wm-actions">
              <button className="wm-btn wm-btn--primary" onClick={handleRefGoTo}>
                Ver mi programa de referidos
              </button>
              <button className="wm-btn wm-btn--ghost" onClick={handleRefClose}>
                Ahora no
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
}

export default WelcomeModals;
